import type { CountryCode } from '../payment-gateways/core/types';
import { env } from '../config/env';

/**
 * Resolution order (see the plan's §1/§3): hostname is the source of truth,
 * since each country is a separate deployment on its own domain. The env
 * var is a local-dev-only fallback — it must never be what production
 * relies on, because that's exactly the "env says X, payload says Y"
 * ambiguity that made the old resolver fragile.
 *
 * TODO(payments): confirm the exact production hostnames with the team —
 * inferred here from the domains named when this plan was scoped
 * (nura.in for India, nurathailand.com for Thailand). Suffix-matched so
 * uat./staging./www. subdomains resolve the same way.
 */
const HOSTNAME_SUFFIXES: Array<[string, CountryCode]> = [
  ['nura.in', 'IN'],
  ['nurathailand.com', 'TH'],
];

const LEGACY_COUNTRY_ALIASES: Record<string, CountryCode> = {
  IND: 'IN',
  IN: 'IN',
  TH: 'TH',
  THA: 'TH',
};

/**
 * Normalizes whatever country string the booking app sends (`IND`, `IN`,
 * `TH`, ...) into the one format the rest of this app uses. Returns null for
 * anything unrecognized rather than guessing.
 */
export function normalizeCountry(
  raw: string | undefined | null
): CountryCode | null {
  if (!raw) return null;
  const normalized = LEGACY_COUNTRY_ALIASES[raw.trim().toUpperCase()];

  return normalized ?? null;
}

function resolveFromHostname(hostname: string): CountryCode | null {
  const match = HOSTNAME_SUFFIXES.find(([suffix]) => hostname.endsWith(suffix));

  return match?.[1] ?? null;
}

function resolveFromQueryOverride(search: URLSearchParams): CountryCode | null {
  // QA-only escape hatch — deliberately not documented for end users.
  return normalizeCountry(search.get('__country'));
}

/**
 * Resolves the active country once, in priority order:
 * hostname → explicit QA query override → env fallback (local dev only).
 * Returns null if nothing resolves — callers must treat that as an
 * unrecoverable config error, not silently render a blank page (§1).
 */
export function resolveCountry(
  hostname: string,
  search: URLSearchParams
): CountryCode | null {
  return (
    resolveFromHostname(hostname) ??
    resolveFromQueryOverride(search) ??
    normalizeCountry(env.VITE_COUNTRY)
  );
}
